/**
 * Cart Footer Upsell Custom Element
 * Handles displaying upsell products in the cart footer when trigger products are present
 */

if (!customElements.get('cart-footer-upsell')) {
  class CartFooterUpsell extends HTMLElement {
    constructor() {
      super();
      this.productInCart = this.dataset.productInCart === 'true';
      this.hasProduct = this.dataset.hasProduct === 'true';
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.triggerProductIds = this.dataset.triggerProductIds ? this.dataset.triggerProductIds.split(',').filter(id => id) : [];
      this.triggerVariantIds = this.dataset.triggerVariantIds ? this.dataset.triggerVariantIds.split(',').filter(id => id) : [];
      this.dismissedKey = 'cart-footer-upsell-dismissed';
      this.triggerInCartKey = 'cart-footer-upsell-trigger-in-cart';
      this.cartDrawer = document.querySelector('cart-drawer');
    }

    connectedCallback() {

      // Prevent double initialization
      if (this.hasAttribute('data-initialized')) {
        this.updateDisplay();
        return;
      }

      // Remove any other cart-footer-upsell elements (old instances from AJAX updates)
      document.querySelectorAll('cart-footer-upsell').forEach((el) => {
        if (el !== this) {
          el.remove();
        }
      });

      // Mark as initialized FIRST to prevent race conditions
      this.setAttribute('data-initialized', 'true');

      try {
        // Initialize state management for trigger tracking
        const triggerState = sessionStorage.getItem(this.triggerInCartKey);
        const isNewComponent = !this.hasAttribute('data-trigger-state-initialized');


        // If this is a new component or trigger state is not set, clear dismissals
        if (isNewComponent || triggerState === null) {
          this.clearAllDismissed();
          sessionStorage.setItem(this.triggerInCartKey, 'true');
          this.setAttribute('data-trigger-state-initialized', 'true');
        }

        // Initialize immediately - don't wait for animation frame
        this.updateDisplay();
        this.bindEvents();
        this.observeCartChanges();

        // Also update on next frame as safety measure
        requestAnimationFrame(() => {
          this.updateDisplay();
        });
      } catch (error) {
        console.error('❌ Error initializing CartFooterUpsell:', error);
      }
    }

    disconnectedCallback() {
    }

    getDismissedProducts() {
      try {
        return JSON.parse(sessionStorage.getItem(this.dismissedKey)) || [];
      } catch {
        return [];
      }
    }

    setDismissedProduct(productId) {
      const dismissed = this.getDismissedProducts();
      if (!dismissed.includes(productId)) {
        dismissed.push(productId);
        sessionStorage.setItem(this.dismissedKey, JSON.stringify(dismissed));
      }
    }

    clearAllDismissed() {
      sessionStorage.removeItem(this.dismissedKey);
    }

    isProductDismissed(productId) {
      return this.getDismissedProducts().includes(productId);
    }

    updateDisplay() {

      // Re-read data attributes in case they changed after AJAX update
      this.productInCart = this.dataset.productInCart === 'true';
      this.hasProduct = this.dataset.hasProduct === 'true';
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.triggerProductIds = this.dataset.triggerProductIds ? this.dataset.triggerProductIds.split(',').filter(id => id) : [];
      this.triggerVariantIds = this.dataset.triggerVariantIds ? this.dataset.triggerVariantIds.split(',').filter(id => id) : [];

      // Check trigger state - if component exists, trigger must be in cart
      const triggerState = sessionStorage.getItem(this.triggerInCartKey);
      const triggerWasInCart = triggerState === 'true';
      const triggerWasRemoved = triggerState === 'false';
      const isNewComponent = !this.hasAttribute('data-trigger-state-initialized');

      // Clear dismissals if:
      // 1. This is a new component (just created = trigger just added)
      // 2. Trigger was removed before and now it's back (triggerWasRemoved = true)
      // 3. Trigger state is not set (first time)
      if (isNewComponent || triggerWasRemoved || triggerState === null) {
        this.clearAllDismissed();
        sessionStorage.setItem(this.triggerInCartKey, 'true');
        this.setAttribute('data-trigger-state-initialized', 'true');
      } else if (triggerWasInCart) {
        // Trigger was already in cart - just mark as initialized
        this.setAttribute('data-trigger-state-initialized', 'true');
      }

      // Check if product has been dismissed (user added then removed)
      // This check happens AFTER clearing dismissals if trigger was just added
      const productDismissed = this.productId && this.isProductDismissed(this.productId);

      // Show upsell if:
      // 1. Product is configured and available
      // 2. Product is NOT already in cart
      // 3. Product has NOT been dismissed (user added then removed it)
      const shouldShow =
        this.hasProduct &&
        !this.productInCart &&
        !productDismissed;

      // Apply display - always use important to override inline styles
      if (shouldShow) {
        this.style.setProperty('display', 'block');
        // Also ensure visibility
        this.style.setProperty('visibility', 'visible');
        this.style.setProperty('opacity', '1', 'important');
      } else {
        this.style.setProperty('display', 'none');
      }
    }

    bindEvents() {
      const addButton = this.querySelector('.cart-footer-upsell__add-btn');

      if (addButton) {
        addButton.addEventListener('click', (e) => this.handleAddToCart(e));
      }
    }

    observeCartChanges() {
      // Listen for cart updates to detect when upsell product is removed
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Check for trigger/product changes first
          this.checkForRemovedUpsell(event.cartData);
          // Update display immediately
          this.updateDisplay();
          // Also update on next frame for safety
          requestAnimationFrame(() => {
            this.updateDisplay();
          });
        });
      }

      // Also listen for custom cart refresh events
      document.addEventListener('cart:refresh', () => {
        this.updateDisplay();
        requestAnimationFrame(() => {
          this.updateDisplay();
        });
      });
    }

    checkForRemovedUpsell(cartData) {
      if (!cartData || !cartData.items) return;

      // Collect all product IDs, variant IDs, and component IDs from cart
      // This includes main items AND item_components (metaobjects/bundles)
      const cartProductIds = new Set();
      const cartVariantIds = new Set();

      cartData.items.forEach(item => {
        // Add main item product and variant IDs (quantity doesn't matter - just presence)
        if (item.product_id) cartProductIds.add(String(item.product_id));
        if (item.variant_id) cartVariantIds.add(String(item.variant_id));

        // Check item_components for bundle/metaobject products
        // These are product references within metaobjects
        if (item.item_components && Array.isArray(item.item_components)) {
          item.item_components.forEach(component => {
            // Add product IDs from components (metaobject product references)
            if (component.product_id) {
              cartProductIds.add(String(component.product_id));
            }
            // Add variant IDs from components
            if (component.variant_id) {
              cartVariantIds.add(String(component.variant_id));
            }
          });
        }
      });

      // Check if any trigger product is still in the cart
      // Check both product IDs and variant IDs (for variants and metaobject products)
      // Quantity doesn't matter - just presence
      let triggerStillInCart = false;

      // Check trigger product IDs (main products and metaobject references)
      for (const triggerId of this.triggerProductIds) {
        if (cartProductIds.has(triggerId)) {
          triggerStillInCart = true;
          break;
        }
      }

      // Also check trigger variant IDs (handles cases where a variant is added directly or via metaobject)
      if (!triggerStillInCart && this.triggerVariantIds.length > 0) {
        for (const triggerVariantId of this.triggerVariantIds) {
          if (cartVariantIds.has(triggerVariantId)) {
            triggerStillInCart = true;
            break;
          }
        }
      }

      // Check if trigger was previously in cart
      const triggerWasInCart = sessionStorage.getItem(this.triggerInCartKey) === 'true';

      if (triggerStillInCart) {
        // Trigger is in cart
        // If it wasn't in cart before, clear dismissals (trigger just added back)
        if (!triggerWasInCart) {
          this.clearAllDismissed();
        }
        sessionStorage.setItem(this.triggerInCartKey, 'true');
      } else {
        // Trigger not in cart
        if (triggerWasInCart) {
          // Trigger was removed - mark as removed so we know to clear dismissals when it's added back
          sessionStorage.setItem(this.triggerInCartKey, 'false');
        }
        // Component will be removed anyway since trigger is gone
        return;
      }

      // Check if upsell product was in cart but now removed (upsell was added then removed)
      const productStillInCart = cartProductIds.has(this.productId) || cartVariantIds.has(this.variantId);

      if (this.productId && this.productInCart && !productStillInCart) {
        this.setDismissedProduct(this.productId);
        this.style.display = 'none';
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: 'cart-drawer',
          selector: '#CartDrawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    async handleAddToCart(e) {
      e.preventDefault();
      const button = e.currentTarget;
      const variantId = button.dataset.variantId;
      const productTitle = button.dataset.productTitle;
      const productId = button.dataset.productId;

      if (!variantId) return;

      button.classList.add('loading');
      button.disabled = true;

      try {
        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        };

        const sections = this.getSectionsToRender().map(s => s.id);

        config.body = JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1,
            properties: {
              '_footer_upsell': 'true'
            }
          }],
          sections: sections,
          sections_url: window.location.pathname
        });

        const addUrl = (window.routes && window.routes.cart_add_url) || (window.Shopify.routes.root + 'cart/add.js');
        const response = await fetch(addUrl, config);
        const result = await response.json();

        if (response.ok) {
          // Render cart updates using theme's method
          if (this.cartDrawer && result.sections) {
            this.cartDrawer.renderContents(result);
          } else {
            await this.refreshCartDrawer();
          }

          // Show toast notification
          this.showToast(productTitle);
        } else {
          console.error('Failed to add to cart:', result);
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        button.classList.remove('loading');
        button.disabled = false;
      }
    }

    async refreshCartDrawer() {
      try {
        const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawerInner = doc.querySelector('.drawer__inner');
        const currentDrawerInner = document.querySelector('cart-drawer .drawer__inner');

        if (newDrawerInner && currentDrawerInner) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }

        const bubbleResponse = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
        const bubbleHtml = await bubbleResponse.text();
        const bubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');
        const newBubble = bubbleDoc.querySelector('.shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');

        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      } catch (error) {
        console.error('Error refreshing cart drawer:', error);
      }
    }

    showToast(productTitle) {
      document.dispatchEvent(new CustomEvent('ed:toast:notification:trigger', {
        detail: {
          duration: 3000,
          text: `${productTitle} wurde hinzugefügt`,
          type: 'info'
        }
      }));
    }
  }

  customElements.define('cart-footer-upsell', CartFooterUpsell);
  
  // Global function to manually initialize all cart-footer-upsell elements
  window.initCartFooterUpsells = function() {
    document.querySelectorAll('cart-footer-upsell').forEach((el) => {
      if (!el.hasAttribute('data-initialized')) {
        if (el.connectedCallback) {
          el.connectedCallback();
        }
      } else {
        if (el.updateDisplay) {
          el.updateDisplay();
        }
      }
    });
  };

  // CRITICAL: Listen for cart updates globally and force upsell to show
  // This ensures the upsell appears even if connectedCallback doesn't fire properly
  function setupGlobalCartListener() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Use setTimeout to ensure DOM has been updated
        setTimeout(() => {
          const upsellElement = document.querySelector('cart-footer-upsell');
          if (upsellElement) {
            window.initCartFooterUpsells();
          } else {
          }
        }, 150);
      });
    } else {
      // PubSub not ready yet, wait a bit and try again
      setTimeout(setupGlobalCartListener, 100);
    }
  }

  // Start setting up the listener
  setupGlobalCartListener();

  // Auto-initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.initCartFooterUpsells);
  } else {
    setTimeout(window.initCartFooterUpsells, 100);
  }

  // Watch for dynamically added elements (e.g., after AJAX cart updates)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the added node is our custom element
          if (node.tagName && node.tagName.toLowerCase() === 'cart-footer-upsell') {
            // Wait a bit for the element to be fully connected
            setTimeout(() => {
              // The connectedCallback should have been called automatically
              // But if not, manually trigger initialization
              if (node.connectedCallback && !node.hasAttribute('data-initialized')) {
                node.connectedCallback();
                node.setAttribute('data-initialized', 'true');
              }
            }, 50);
          }
          // Also check for our element inside added nodes
          if (node.querySelectorAll) {
            const upsellElements = node.querySelectorAll('cart-footer-upsell');
            upsellElements.forEach((el) => {
              setTimeout(() => {
                if (el.connectedCallback && !el.hasAttribute('data-initialized')) {
                  el.connectedCallback();
                  el.setAttribute('data-initialized', 'true');
                }
              }, 50);
            });
          }
        }
      });
    });
  });

  // Start observing when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
